/**
 * WelcomeScreen — bienvenida conversacional sin menú jerárquico.
 *
 * Refactor 2026-05-08: los 4 cards rígidos de antes (síntoma/marca/PA/receta)
 * forzaban a la farmacéutica a clasificar la consulta antes de escribirla.
 * El L1 classifier del backend ya detecta el flow automáticamente sobre
 * cualquier query, así que pre-categorizar visualmente no aporta info y
 * agrega fricción.
 *
 * Nuevo diseño: saludo conversacional + chips de ejemplos sutiles que
 * pre-rellenan el composer al tap. Sin jerarquía visual entre flows. La
 * farmacéutica escribe directo lo que escucha del cliente.
 *
 * 2026-05-12 — chips contextuales por perfil. Cuando se selecciona Adulto/
 * Niño/Embarazo/Lactancia, las burbujas centrales cambian a las consultas
 * más comunes en mostrador para ese perfil. Curadas con clínicos +
 * patrón empírico Hipermaxi.
 */
import { useEffect, useRef } from 'react'
import type { ProfileType } from '../types/profile'

interface Props {
  /** Click en una burbuja del welcome → ENVÍA la consulta directo, sin paso
   *  intermedio de "escribir en input + apretar send". 2026-05-12 fix UX
   *  reportado por Elvio: la regente clickeaba la burbuja, veía que el
   *  texto aparecía abajo en el textarea, y esperaba respuesta. Como no
   *  pasaba nada hasta apretar send manualmente, lo percibía como
   *  "se corta y queda pensando". Las burbujas son atajos directos. */
  onSend: (query: string) => void
  profile: ProfileType | null
}

// Notas como las escribiría la farmacéutica en mostrador — 3ra persona
// sobre lo que reporta el cliente. Mix de flows: síntoma, marca, PA,
// pediátrico, receta. Sin jerarquía (el L1 classifier detecta el flow
// automáticamente sobre cualquier texto libre).
const DEFAULT_CHIPS: string[] = [
  'dolor de cabeza',
  'fiebre alta',
  'cliente con tos',
  'niño con diarrea',
  'pide Pironal Forte',
  'pide amoxicilina',
  'trae receta del médico',
  'no puede dormir',
]

// Consultas más frecuentes adulto (analgésicos, GI, sueño, gripe, espalda).
const ADULT_CHIPS: string[] = [
  'dolor de cabeza',
  'gastritis con ardor',
  'dolor de espalda fuerte',
  'resfriado y mocos',
  'tos seca de noche',
  'cliente con migraña',
  'dolor menstrual',
  'no puede dormir',
  'pide Pironal Forte',
  'trae receta del médico',
]

// Pediátrico — fiebre/tos/diarrea/alergia/otitis/cólico son los top en
// mostrador. Forma JBE/gotas implícita; backend filtra loperamida + opioides.
const CHILD_CHIPS: string[] = [
  'fiebre del niño',
  'niño con tos productiva',
  'diarrea aguda del bebé',
  'dolor de oído',
  'mocos y congestión',
  'cólico abdominal',
  'alergia con sarpullido',
  'niño con vómitos',
  'conjuntivitis del bebé',
  'pide paracetamol jarabe',
]

// Embarazo — náuseas, acidez, cefalea (paracetamol único OTC), estreñimiento,
// resfriado sin AINEs/pseudoefedrina, dolor lumbar.
const PREGNANCY_CHIPS: string[] = [
  'dolor de cabeza',
  'náuseas matutinas',
  'acidez y reflujo',
  'estreñimiento varios días',
  'dolor lumbar',
  'calambres en piernas',
  'resfriado sin fiebre',
  'congestión nasal',
  'ardor estomacal después de comer',
  'consulta por receta médica',
]

// Lactancia — mastitis, cefalea, alergia estacional, diarrea, dolor lumbar,
// dolor menstrual postparto. NO aspirina ni ketorolaco (filtros backend).
const LACTATION_CHIPS: string[] = [
  'mastitis dolorosa',
  'dolor de cabeza',
  'alergia estacional',
  'diarrea de la mamá',
  'dolor lumbar postparto',
  'congestión nasal',
  'gripe sin fiebre',
  'dolor menstrual',
  'cansancio extremo',
  'pide ibuprofeno',
]

function chipsForProfile(profile: ProfileType | null): string[] {
  if (profile === null) return DEFAULT_CHIPS
  if (profile === 'adult') return ADULT_CHIPS
  if (profile === 'pregnancy') return PREGNANCY_CHIPS
  if (profile === 'lactation') return LACTATION_CHIPS
  // child_3y / child_5y / child_8y / child_12y → todos pediátricos
  return CHILD_CHIPS
}

export function WelcomeScreen({ onSend, profile }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const chips = chipsForProfile(profile)

  // Re-shuffle de chips on profile change para que el usuario vea variedad —
  // si re-entrás al mismo perfil el orden no es siempre el mismo.
  useEffect(() => {
    const root = containerRef.current
    if (!root) return
    const chipNodes = Array.from(root.querySelectorAll<HTMLButtonElement>('.example-chip'))
    chipNodes.sort(() => Math.random() - 0.5).forEach((n) => n.parentElement?.appendChild(n))
  }, [profile])

  return (
    <div
      ref={containerRef}
      className="grid content-center px-6 py-8 gap-8 overflow-y-auto"
    >
      <div className="text-center grid gap-3 max-w-md mx-auto">
        <h1
          className="text-[26px] italic leading-tight"
          style={{
            fontFamily: 'var(--font-serif)',
            color: 'var(--color-text)',
          }}
        >
          Contame qué necesita el cliente
        </h1>
        <p
          className="text-[14px] leading-relaxed"
          style={{ color: 'var(--color-text-muted)' }}
        >
          Escribí lo que escuchás — síntoma, marca, principio activo o receta.
          Yo me encargo del resto.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
        {chips.map((chip) => (
          <button
            key={chip}
            type="button"
            onClick={() => onSend(chip)}
            className="example-chip text-left transition-all"
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-text-muted)',
              fontSize: 13,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--color-brand-soft)'
              e.currentTarget.style.borderColor = 'var(--color-brand)'
              e.currentTarget.style.color = 'var(--color-text)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--color-surface)'
              e.currentTarget.style.borderColor = 'var(--color-border)'
              e.currentTarget.style.color = 'var(--color-text-muted)'
            }}
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  )
}
